import { Button, Input, Typography, CloseAltIcon, CheckIcon } from '@mgmresorts/wcl-lab';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { PolicyUserInfo } from '..';
import { Alert } from '../../../components/ui/Alert';
import { Container } from '../../../components/ui/Container';
import { NavigationLink } from '../../../components/ui/Link';
import { Logo } from '../../../components/ui/Logo';
import { useContent } from '../../../hooks/useContent';
import { useFieldValidation } from '../../../hooks/useFieldValidation';
import { useInput } from '../../../hooks/useInput';
import { usePasswordInput } from '../../../hooks/usePasswordInput';
import { useEvent } from '../../../utils/analytics/useEvent';
import { MaxLengths } from '../../../utils/constants';
import { PasswordValidationHint } from '../styled';

export function ResetPasswordForm({
  isLoading,
  resetPassword,
  error,
  userInfo,
}: {
  isLoading: boolean;
  resetPassword: (nextPassword: string) => void;
  error: string | null;
  userInfo?: PolicyUserInfo;
}) {
  const trackEvent = useEvent();
  const { validateField, invalidFields } = useFieldValidation();
  const { getContentByPath } = useContent();
  const [trackedPassword, setTrackedPassword] = useState('');
  const [trackedConfirmPassword, setTrackedConfirmPassword] = useState('');
  const successColor = '#00803C';

  const {
    value: password,
    bind: bindPasswordField,
    isValid: isValidPassword,
    validations: passwordValidations,
  } = usePasswordInput('', 'password', userInfo || {}, validateField);

  const { value: passwordConfirm, bind: bindPasswordConfirmField } = useInput(
    '',
    'passwordConfirm',
    undefined,
    validateField,
  );

  const hasPasswordValidationError = invalidFields.includes('password') || !isValidPassword;
  const hasPasswordConfirmValidationError =
    invalidFields.includes('passwordConfirm') || (password && password !== passwordConfirm);

  const canProcessRequest = useMemo(
    () =>
      !!(
        isValidPassword &&
        password &&
        passwordConfirm &&
        password === passwordConfirm &&
        !isLoading &&
        !invalidFields.length
      ),
    [invalidFields.length, isLoading, isValidPassword, password, passwordConfirm],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      let color = '#E02D21';
      let Icon = CloseAltIcon;
      if (!password.length) {
        color = '#5D5D5D';
      } else if (isValid) {
        color = successColor;
        Icon = CheckIcon;
      }
      if (!password.length) {
        return (
          <PasswordValidationHint>
            <span style={{ margin: '0 6px' }}>●</span>
            <Typography variant='paragraph'>{hint}</Typography>
          </PasswordValidationHint>
        );
      }
      return (
        <PasswordValidationHint>
          <Icon color={color} size={20} width={40} />
          <Typography variant='paragraph' color={color}>
            {hint}
          </Typography>
        </PasswordValidationHint>
      );
    },
    [password.length, passwordValidations],
  );

  useEffect(() => {
    const isInvalidPassword = invalidFields.includes('password');

    if (isInvalidPassword) {
      const errorPath = isInvalidPassword
        ? 'resetPassword.steps.reset.fields.password.validation.empty'
        : 'resetPassword.steps.reset.fields.password.validation.passwordPolicy';

      const errorMessage = getContentByPath(errorPath) as string;
      if (trackedPassword !== errorMessage) {
        trackEvent(
          'identity – reset password - credentials input error',
          'reset password credentials input error',
          'resetpinputerror',
          {
            errorField: 'New Password',
            errorDisplayed: errorMessage,
          },
        );
        setTrackedPassword(errorMessage);
      }
    } else {
      setTrackedPassword('');
    }

    const isPasswordEmpty = !password.length && !passwordConfirm.length;
    const isPasswordMismatch = password.length > 0 && passwordConfirm.length > 0 && password !== passwordConfirm;
    const isInvalidConfirmPassword = invalidFields.includes('passwordConfirm');

    if (isPasswordEmpty) {
      return;
    } else if (isInvalidConfirmPassword || isPasswordMismatch) {
      const errorPath = isInvalidConfirmPassword
        ? 'resetPassword.steps.reset.fields.passwordConfirm.validation.empty'
        : 'resetPassword.steps.reset.fields.passwordConfirm.validation.mismatch';

      const errorMessage = getContentByPath(errorPath) as string;
      if (trackedConfirmPassword !== errorMessage) {
        trackEvent(
          'identity – reset password - credentials input error',
          'reset password credentials input error',
          'resetpinputerror',
          {
            errorField: 'Re-type Password',
            errorDisplayed: errorMessage,
          },
        );
        setTrackedConfirmPassword(errorMessage);
      }
    } else {
      setTrackedConfirmPassword('');
    }
  }, [
    getContentByPath,
    invalidFields,
    isValidPassword,
    password,
    passwordConfirm,
    trackEvent,
    trackedConfirmPassword,
    trackedPassword,
  ]);

  const getPasswordFieldError = useCallback(() => {
    if (invalidFields.includes('password')) {
      return getContentByPath('resetPassword.steps.reset.fields.password.validation.empty');
    }

    if (!isValidPassword) {
      return getContentByPath('resetPassword.steps.reset.fields.password.validation.passwordPolicy');
    }

    return '';
  }, [invalidFields, isValidPassword, getContentByPath]);

  const getPasswordConfirmationFieldError = useCallback(() => {
    if (!password.length && !passwordConfirm.length) {
      return;
    }

    if (invalidFields.includes('passwordConfirm')) {
      return getContentByPath('resetPassword.steps.reset.fields.passwordConfirm.validation.empty');
    }

    if (password.length > 0 && passwordConfirm.length > 0 && password !== passwordConfirm) {
      return getContentByPath('resetPassword.steps.reset.fields.passwordConfirm.validation.mismatch');
    }

    return '';
  }, [getContentByPath, invalidFields, password, passwordConfirm]);

  const passwordError = useMemo(
    () => (invalidFields.includes('password') || (password && !isValidPassword) ? getPasswordFieldError() : undefined),
    [getPasswordFieldError, invalidFields, isValidPassword, password],
  );

  const passwordConfirmError = useMemo(
    () =>
      invalidFields.includes('passwordConfirm') || passwordConfirm ? getPasswordConfirmationFieldError() : undefined,
    [getPasswordConfirmationFieldError, invalidFields, passwordConfirm],
  );

  return (
    <Container height='auto'>
      <Logo className='identity__page-logo' />
      <Typography variant='h1' className='identity__page-heading' testId='page-title'>
        {getContentByPath('resetPassword.steps.reset.title')}
      </Typography>
      {error && <Alert marginBottom='0' text={error} data-testid='reset-password-error' />}
      {!isValidPassword && (
        <div className='identity__password-policy'>
          Password Requirements
          <div className='identity__password-hits-wrapper' data-testid='hints-wrapper'>
            {passwordValidationHint(
              'length',
              getContentByPath('resetPassword.steps.reset.fields.password.validation.lengthHint'),
            )}
            {passwordValidationHint(
              'number',
              getContentByPath('resetPassword.steps.reset.fields.password.validation.numberHint'),
            )}
            {passwordValidationHint(
              'uppercase',
              getContentByPath('resetPassword.steps.reset.fields.password.validation.uppercaseHint'),
            )}
            {passwordValidationHint(
              'lowercase',
              getContentByPath('resetPassword.steps.reset.fields.password.validation.lowercaseHint'),
            )}
            {passwordValidationHint(
              'specialCharacter',
              getContentByPath('resetPassword.steps.reset.fields.password.validation.specialCharacterHint'),
            )}
            {passwordValidationHint(
              'firstName',
              getContentByPath('resetPassword.steps.reset.fields.password.validation.firstName'),
            )}
            {passwordValidationHint(
              'lastName',
              getContentByPath('resetPassword.steps.reset.fields.password.validation.lastName'),
            )}
          </div>
        </div>
      )}
      {password && isValidPassword && (
        <div className='identity__password-success-info'>
          <CheckIcon size={20} width={40} color={successColor} />
          <Typography
            variant='paragraph'
            className='identity__password-policy'
            testId='reset-password-all-met'
            color={successColor}
          >
            {getContentByPath('resetPassword.steps.reset.fields.password.validation.allMet')}
          </Typography>
        </div>
      )}
      <Input
        type='password'
        id='password'
        testId='password'
        label={getContentByPath('resetPassword.steps.reset.fields.password.label')}
        aria-label={getContentByPath('resetPassword.steps.reset.fields.password.label')}
        disabled={isLoading}
        className={`identity__field password ${hasPasswordValidationError ? 'with-error' : ''}`}
        error={!!passwordError}
        hint={passwordError as string}
        {...bindPasswordField}
        maxLength={MaxLengths.Password}
      />
      <Input
        type='password'
        id='passwordConfirm'
        testId='passwordConfirm'
        label={getContentByPath('resetPassword.steps.reset.fields.passwordConfirm.label')}
        aria-label={getContentByPath('resetPassword.steps.reset.fields.passwordConfirm.label')}
        disabled={isLoading}
        className={`identity__field password-confirm ${hasPasswordConfirmValidationError ? 'with-error' : ''}`}
        error={!!passwordConfirmError}
        hint={passwordConfirmError as string}
        {...bindPasswordConfirmField}
        maxLength={MaxLengths.Password}
      />
      <Button
        sticky
        ariaLabel={getContentByPath('resetPassword.steps.reset.buttons.primaryAction.label')}
        onClick={() => resetPassword(password)}
        onKeyPress={e => {
          e.preventDefault();
          if (e.key === 'Enter') {
            resetPassword(password);
          }
        }}
        className='identity__form-submit'
        disabled={!canProcessRequest}
        loading={false}
      >
        <Typography>{getContentByPath('resetPassword.steps.reset.buttons.primaryAction.label')}</Typography>
      </Button>

      <NavigationLink
        className='identity__secondary-action center'
        path='/'
        onClick={async () => {
          await trackEvent(
            'identity – reset password - back to sign in',
            'back to sign in',
            'resetpbacktosignin',
            {},
            true,
          );
        }}
      >
        {getContentByPath('resetPassword.steps.reset.buttons.secondaryAction.label')}
      </NavigationLink>
    </Container>
  );
}

ResetPasswordForm.displayName = 'ResetPasswordForm';
